/**
 * Transaction Rules Matcher
 * Pure matching logic — no Firebase dependencies, easy to test.
 * Applies transaction rules at display-time without persisting changes.
 */

import type { Transaction, TransactionRule } from '../../types';
import { getPrimaryFromDetailed, getPlaidCategoryIconUrl } from '../../constants/pfmCategories';

/**
 * Check whether a single transaction matches a single rule.
 */
export const transactionMatchesRule = (tx: Transaction, rule: TransactionRule): boolean => {
  if (!rule.isActive) return false;

  if (rule.matchField === 'merchant') {
    const merchant = tx.merchant.toLowerCase();
    const value = rule.matchValue; // already stored lowercase

    if (rule.matchType === 'exact') {
      return merchant === value;
    }
    if (rule.matchType === 'contains') {
      return merchant.includes(value);
    }
  }

  return false;
};

/**
 * Find all active rules that match a given transaction.
 */
export const findMatchingRules = (tx: Transaction, rules: TransactionRule[]): TransactionRule[] => {
  return rules.filter(rule => transactionMatchesRule(tx, rule));
};

/**
 * Pick the highest-priority rule for a given action type from matching rules.
 * Priority: exact match > contains match. Within the same match type, most recently created wins.
 * Rules are already ordered by createdAt desc, so the first match at the highest priority level wins.
 */
const pickBestRule = (
  matching: TransactionRule[],
  hasAction: (rule: TransactionRule) => boolean,
): TransactionRule | undefined => {
  // Try exact match first
  const exact = matching.find(r => r.matchType === 'exact' && hasAction(r));
  if (exact) return exact;
  // Fall back to contains
  return matching.find(r => r.matchType === 'contains' && hasAction(r));
};

/**
 * Apply rules to a single transaction.
 * Priority: per-transaction user override > exact match rule > contains match rule.
 * Reflection and category are resolved independently from the best rule for each.
 * Returns a new Transaction object (never mutates the original).
 */
export const applyRulesToTransaction = (tx: Transaction, rules: TransactionRule[]): Transaction => {
  const matching = findMatchingRules(tx, rules);
  if (matching.length === 0) return tx;

  const result: Transaction = { ...tx };

  // Apply reflection (user's explicit reflection always wins)
  if (!tx.reflection) {
    const reflectionRule = pickBestRule(matching, r => r.actions.autoReflection !== undefined);
    if (reflectionRule) {
      result.reflection = reflectionRule.actions.autoReflection;
      result.appliedByRule = true;
      result.reflectionAppliedByRule = true;
    }
  }

  // Apply category (per-transaction user override always wins)
  if (!tx.userCategoryOverride) {
    const categoryRule = pickBestRule(
      matching,
      r => r.actions.categoryDetailedOverride !== undefined || r.actions.categoryOverride !== undefined,
    );
    if (categoryRule) {
      if (categoryRule.actions.categoryDetailedOverride) {
        const newPrimary = getPrimaryFromDetailed(categoryRule.actions.categoryDetailedOverride);
        result.categoryDetailed = categoryRule.actions.categoryDetailedOverride;
        result.category = newPrimary;
        result.appliedByRule = true;
        // Replace stale Plaid merchant logo with the new category's icon
        result.logoUrl = getPlaidCategoryIconUrl(newPrimary);
      } else if (categoryRule.actions.categoryOverride) {
        result.category = categoryRule.actions.categoryOverride;
        result.appliedByRule = true;
        result.logoUrl = getPlaidCategoryIconUrl(categoryRule.actions.categoryOverride);
      }
    }
  }

  return result;
};

/**
 * Apply rules to an array of transactions. Returns a new array.
 */
export const applyRulesToTransactions = (
  transactions: Transaction[],
  rules: TransactionRule[],
): Transaction[] => {
  if (rules.length === 0) return transactions;
  return transactions.map(tx => applyRulesToTransaction(tx, rules));
};
